/**
 * Guided-form ⇆ CSAF 2.0 mapping — full-fidelity.
 *
 * `AdvisoryForm` is a flat, UI-friendly model covering the fields the guided
 * form exposes directly. To support the *entire* CSAF 2.0 schema (as reviewed
 * and approved for full Secvisogram parity), the mapping is **lossless**:
 *
 *  - `csafToForm` captures the original CSAF document (and the original object
 *    for every product, vulnerability and remediation) on hidden `_source`
 *    fields.
 *  - `formToCsaf` starts from a deep clone of that source and *overlays* only
 *    the fields the form manages. Every advanced field the form does not have a
 *    dedicated input for (product_identification_helper, CVSS v2, flags,
 *    involvements, per-vuln acknowledgments, product_groups, relationships, …)
 *    therefore passes straight through untouched.
 *
 * The result: anything authorable in the raw-JSON tab (the full CSAF surface,
 * schema-validated) survives a round-trip through the guided form. The CSAF
 * JSON remains the single source of truth for validation, storage, JSON export
 * and PDF rendering.
 */

// ---------------------------------------------------------------------------
// Safe accessors + small helpers
// ---------------------------------------------------------------------------
type Json = Record<string, unknown>;

const asObj = (v: unknown): Json =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Json) : {};
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asStr = (v: unknown): string =>
  typeof v === "string" ? v : typeof v === "number" ? String(v) : "";

const clone = <T>(v: T): T =>
  v == null ? v : (JSON.parse(JSON.stringify(v)) as T);

const splitCsv = (s: string): string[] =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const joinCsv = (arr: unknown[]): string =>
  arr.map(asStr).filter(Boolean).join(", ");

const dedupe = (arr: string[]): string[] => [...new Set(arr.filter(Boolean))];

/** Set a string key when non-empty, otherwise remove it. */
const setStr = (obj: Json, key: string, val: string) => {
  const v = val.trim();
  if (v) obj[key] = v;
  else delete obj[key];
};

/** Set an array key when non-empty (deduped), otherwise remove it. */
const setArr = (obj: Json, key: string, arr: string[]) => {
  const v = dedupe(arr);
  if (v.length) obj[key] = v;
  else delete obj[key];
};

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------
export interface AdvisoryFormReference {
  category: string; // "self" | "external"
  summary: string;
  url: string;
}

export interface AdvisoryFormId {
  systemName: string;
  text: string;
}

export interface AdvisoryFormProduct {
  id: string; // CSAFPID-000X (stable, auto-generated)
  name: string;
  _source?: Json; // original full_product_name / branch product (helper data)
}

export interface AdvisoryFormRemediation {
  category: string; // vendor_fix | mitigation | workaround | none_available | no_fix_planned
  details: string;
  url: string;
  date: string;
  productIds: string[];
  _source?: Json; // preserves date/entitlements/group_ids/restart_required…
}

/** The eight CSAF product_status states, in template order. */
export const PRODUCT_STATUS_FIELDS = [
  { key: "knownAffected", csaf: "known_affected", label: "Affected" },
  {
    key: "knownNotAffected",
    csaf: "known_not_affected",
    label: "Not affected",
  },
  { key: "fixed", csaf: "fixed", label: "Fixed" },
  { key: "firstAffected", csaf: "first_affected", label: "First affected" },
  { key: "lastAffected", csaf: "last_affected", label: "Last affected" },
  { key: "firstFixed", csaf: "first_fixed", label: "First fixed" },
  { key: "recommended", csaf: "recommended", label: "Recommended" },
  {
    key: "underInvestigation",
    csaf: "under_investigation",
    label: "Investigating",
  },
] as const;

export type ProductStatusKey = (typeof PRODUCT_STATUS_FIELDS)[number]["key"];

export interface AdvisoryFormVulnerability {
  cve: string;
  title: string;
  cweId: string;
  cweName: string;
  description: string;
  cvssVersion: string; // "3.1"
  cvssVector: string;
  cvssScore: string; // kept as string for the input; parsed on map
  // Full 8-state product status (each a list of product ids)
  knownAffected: string[];
  knownNotAffected: string[];
  fixed: string[];
  firstAffected: string[];
  lastAffected: string[];
  firstFixed: string[];
  recommended: string[];
  underInvestigation: string[];
  exploitation: string;
  discoveryDate: string;
  releaseDate: string;
  ids: AdvisoryFormId[];
  references: AdvisoryFormReference[];
  remediations: AdvisoryFormRemediation[];
  _source?: Json; // preserves flags/involvements/acks/cvss_v2/notes…
}

export interface AdvisoryFormAck {
  names: string; // comma-separated
  organization: string;
  summary: string;
  urls: string; // comma-separated
}

export interface AdvisoryFormRevision {
  number: string;
  date: string;
  summary: string;
}

export interface AdvisoryForm {
  // Document
  title: string;
  tlp: string;
  trackingId: string;
  status: string;
  version: string;
  initialReleaseDate: string;
  currentReleaseDate: string;
  aliases: string; // comma-separated tracking.aliases
  publisherName: string;
  publisherNamespace: string;
  publisherContact: string;
  issuingAuthority: string;
  lang: string;
  aggregateSeverityText: string;
  aggregateSeverityNamespace: string;
  distributionText: string;
  // Notes
  rationale: string;
  notice: string;
  disclaimer: string;
  // References
  selfUrl: string;
  documentReferences: AdvisoryFormReference[];
  // Collections
  products: AdvisoryFormProduct[];
  vulnerabilities: AdvisoryFormVulnerability[];
  acknowledgments: AdvisoryFormAck[];
  revisionHistory: AdvisoryFormRevision[];
  // Full source of everything the form does not manage (product_tree branches,
  // product_groups, relationships, extensions, …). Absent for a fresh form.
  _source?: Json;
}

export const DEFAULT_DISCLAIMER =
  "This advisory is provided on an 'as is' basis and does not imply any kind of guarantee or warranty. Your use of the information in this advisory is at your own risk.";

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------
export function emptyReference(category = "external"): AdvisoryFormReference {
  return { category, summary: "", url: "" };
}

export function emptyId(): AdvisoryFormId {
  return { systemName: "", text: "" };
}

export function emptyVulnerability(): AdvisoryFormVulnerability {
  return {
    cve: "",
    title: "",
    cweId: "",
    cweName: "",
    description: "",
    cvssVersion: "3.1",
    cvssVector: "",
    cvssScore: "",
    knownAffected: [],
    knownNotAffected: [],
    fixed: [],
    firstAffected: [],
    lastAffected: [],
    firstFixed: [],
    recommended: [],
    underInvestigation: [],
    exploitation: "",
    discoveryDate: "",
    releaseDate: "",
    ids: [],
    references: [],
    remediations: [],
  };
}

export function emptyRemediation(): AdvisoryFormRemediation {
  return {
    category: "vendor_fix",
    details: "",
    url: "",
    date: "",
    productIds: [],
  };
}

export function emptyAck(): AdvisoryFormAck {
  return { names: "", organization: "", summary: "", urls: "" };
}

export function nextProductId(products: AdvisoryFormProduct[]): string {
  let max = 0;
  for (const p of products) {
    const m = /CSAFPID-(\d+)/.exec(p.id);
    if (m?.[1]) max = Math.max(max, Number.parseInt(m[1], 10));
  }
  return `CSAFPID-${String(max + 1).padStart(4, "0")}`;
}

export function emptyForm(defaults?: {
  publisherName?: string;
  publisherNamespace?: string;
}): AdvisoryForm {
  const now = new Date().toISOString();
  return {
    title: "",
    tlp: "WHITE",
    trackingId: "",
    status: "draft",
    version: "1",
    initialReleaseDate: now,
    currentReleaseDate: now,
    aliases: "",
    publisherName: defaults?.publisherName || "Sandvik",
    publisherNamespace:
      defaults?.publisherNamespace || "https://www.sandvik.com",
    publisherContact: "",
    issuingAuthority: "",
    lang: "en-US",
    aggregateSeverityText: "",
    aggregateSeverityNamespace: "",
    distributionText: "",
    rationale: "",
    notice: "",
    disclaimer: DEFAULT_DISCLAIMER,
    selfUrl: "",
    documentReferences: [],
    products: [],
    vulnerabilities: [],
    acknowledgments: [],
    revisionHistory: [{ number: "1", date: now, summary: "Initial revision" }],
  };
}

// ---------------------------------------------------------------------------
// CVSS helpers
// ---------------------------------------------------------------------------
export function severityEnum(score: number): string {
  if (score >= 9.0) return "CRITICAL";
  if (score >= 7.0) return "HIGH";
  if (score >= 4.0) return "MEDIUM";
  if (score > 0) return "LOW";
  return "NONE";
}

// ---------------------------------------------------------------------------
// form → CSAF (overlay onto preserved source)
// ---------------------------------------------------------------------------
function buildRevisionHistory(form: AdvisoryForm): Json[] {
  const kept = form.revisionHistory.filter((r) => r.number && r.date);
  if (kept.length) {
    return kept.map((r) => ({
      number: r.number,
      date: r.date,
      summary: r.summary || "Revision",
    }));
  }
  return [
    {
      number: form.version || "1",
      date: form.currentReleaseDate,
      summary: "Initial revision",
    },
  ];
}

/** Replace notes of `managed` categories with the form's, preserve the rest. */
function mergeDocNotes(form: AdvisoryForm, srcNotes: unknown[]): Json[] {
  const managed = new Set(["summary", "general", "legal_disclaimer"]);
  const preserved = srcNotes
    .map(asObj)
    .filter((n) => !managed.has(asStr(n.category)));
  const out = [...preserved];
  if (form.rationale.trim())
    out.push({
      category: "summary",
      title: "Rationale",
      text: form.rationale.trim(),
    });
  if (form.notice.trim())
    out.push({
      category: "general",
      title: "Notice",
      text: form.notice.trim(),
    });
  if (form.disclaimer.trim())
    out.push({
      category: "legal_disclaimer",
      title: "Disclaimer",
      text: form.disclaimer.trim(),
    });
  return out;
}

function buildReferences(
  selfUrl: string,
  refs: AdvisoryFormReference[],
): Json[] {
  const out: Json[] = [];
  if (selfUrl.trim())
    out.push({
      category: "self",
      summary: "Canonical CSAF file",
      url: selfUrl.trim(),
    });
  for (const r of refs) {
    if (!r.url.trim()) continue;
    const ref: Json = { category: r.category || "external", url: r.url.trim() };
    if (r.summary.trim()) ref.summary = r.summary.trim();
    out.push(ref);
  }
  return out;
}

function buildAcks(acks: AdvisoryFormAck[]): Json[] {
  return acks
    .filter(
      (a) =>
        a.names.trim() ||
        a.organization.trim() ||
        a.summary.trim() ||
        a.urls.trim(),
    )
    .map((a) => {
      const ack: Json = {};
      const names = splitCsv(a.names);
      if (names.length) ack.names = names;
      if (a.organization.trim()) ack.organization = a.organization.trim();
      if (a.summary.trim()) ack.summary = a.summary.trim();
      const urls = splitCsv(a.urls);
      if (urls.length) ack.urls = urls;
      return ack;
    });
}

function buildProductStatus(v: AdvisoryFormVulnerability): Json {
  const ps: Json = {};
  for (const f of PRODUCT_STATUS_FIELDS) {
    setArr(ps, f.csaf, v[f.key]);
  }
  return ps;
}

function buildScores(
  v: AdvisoryFormVulnerability,
  allProductIds: string[],
  srcScores: unknown[],
): Json[] {
  const score = Number.parseFloat(v.cvssScore);
  const hasCvss = v.cvssVector.trim() !== "" && !Number.isNaN(score);
  if (!hasCvss) return srcScores.map(asObj);

  const scored = dedupe(
    v.knownAffected.length ? v.knownAffected : allProductIds,
  );
  // scores[].products is required (min 1); if nothing to attach, keep source.
  if (!scored.length) return srcScores.map(asObj);

  const first = asObj(srcScores[0]);
  const cvssV3 = {
    ...asObj(first.cvss_v3),
    version: v.cvssVersion || "3.1",
    vectorString: v.cvssVector.trim(),
    baseScore: score,
    baseSeverity: severityEnum(score),
  };
  const merged: Json = { ...first, products: scored, cvss_v3: cvssV3 };
  return [merged, ...srcScores.slice(1).map(asObj)];
}

function buildRemediations(rems: AdvisoryFormRemediation[]): Json[] {
  return rems
    .filter((r) => r.details.trim())
    .map((r) => {
      const rem: Json = { ...asObj(r._source) };
      rem.category = r.category;
      rem.details = r.details.trim();
      setStr(rem, "url", r.url);
      setStr(rem, "date", r.date);
      setArr(rem, "product_ids", r.productIds);
      return rem;
    });
}

function mergeVulnNotes(srcNotes: unknown[], description: string): Json[] {
  const preserved = srcNotes
    .map(asObj)
    .filter((n) => asStr(n.category) !== "description");
  if (description.trim())
    preserved.push({ category: "description", text: description.trim() });
  return preserved;
}

function mergeThreats(srcThreats: unknown[], exploitation: string): Json[] {
  const preserved = srcThreats
    .map(asObj)
    .filter((t) => asStr(t.category) !== "exploit_status");
  if (exploitation.trim())
    preserved.push({
      category: "exploit_status",
      details: exploitation.trim(),
    });
  return preserved;
}

function buildVuln(
  v: AdvisoryFormVulnerability,
  allProductIds: string[],
): Json {
  const out: Json = clone(asObj(v._source));

  setStr(out, "cve", v.cve);
  setStr(out, "title", v.title);

  if (v.cweId.trim())
    out.cwe = { id: v.cweId.trim(), name: v.cweName.trim() || v.cweId.trim() };
  else delete out.cwe;

  const notes = mergeVulnNotes(asArr(out.notes), v.description);
  if (notes.length) out.notes = notes;
  else delete out.notes;

  const ps = buildProductStatus(v);
  if (Object.keys(ps).length) out.product_status = ps;
  else delete out.product_status;

  const ids = v.ids
    .filter((i) => i.text.trim())
    .map((i) => ({
      system_name: i.systemName.trim() || "unknown",
      text: i.text.trim(),
    }));
  if (ids.length) out.ids = ids;
  else delete out.ids;

  const refs = buildReferences("", v.references);
  if (refs.length) out.references = refs;
  else delete out.references;

  setStr(out, "discovery_date", v.discoveryDate);
  setStr(out, "release_date", v.releaseDate);

  const scores = buildScores(v, allProductIds, asArr(out.scores));
  if (scores.length) out.scores = scores;
  else delete out.scores;

  const rems = buildRemediations(v.remediations);
  if (rems.length) out.remediations = rems;
  else delete out.remediations;

  const threats = mergeThreats(asArr(out.threats), v.exploitation);
  if (threats.length) out.threats = threats;
  else delete out.threats;

  return out;
}

export function formToCsaf(form: AdvisoryForm): Json {
  const out: Json = clone(asObj(form._source));
  const srcDoc = asObj(out.document);

  // ---- document --------------------------------------------------------
  const document: Json = { ...srcDoc };
  document.category = asStr(srcDoc.category) || "csaf_security_advisory";
  document.csaf_version = "2.0";
  document.title = form.title;
  document.lang = form.lang || "en-US";

  const publisher: Json = { ...asObj(srcDoc.publisher) };
  publisher.category = asStr(asObj(srcDoc.publisher).category) || "vendor";
  publisher.name = form.publisherName;
  publisher.namespace = form.publisherNamespace;
  setStr(publisher, "contact_details", form.publisherContact);
  setStr(publisher, "issuing_authority", form.issuingAuthority);
  document.publisher = publisher;

  const tracking: Json = { ...asObj(srcDoc.tracking) };
  tracking.id = form.trackingId;
  tracking.status = form.status;
  tracking.version = form.version || "1";
  tracking.initial_release_date = form.initialReleaseDate;
  tracking.current_release_date = form.currentReleaseDate;
  tracking.revision_history = buildRevisionHistory(form);
  setArr(tracking, "aliases", splitCsv(form.aliases));
  const srcGenerator = asObj(asObj(srcDoc.tracking).generator);
  tracking.generator = Object.keys(srcGenerator).length
    ? srcGenerator
    : {
        engine: { name: "ProdAtlas", version: "1.0" },
        date: form.currentReleaseDate,
      };
  document.tracking = tracking;

  const distribution: Json = { ...asObj(srcDoc.distribution) };
  if (form.tlp)
    distribution.tlp = {
      ...asObj(asObj(srcDoc.distribution).tlp),
      label: form.tlp,
    };
  setStr(distribution, "text", form.distributionText);
  if (Object.keys(distribution).length) document.distribution = distribution;
  else delete document.distribution;

  if (form.aggregateSeverityText.trim()) {
    const agg: Json = { text: form.aggregateSeverityText.trim() };
    if (form.aggregateSeverityNamespace.trim())
      agg.namespace = form.aggregateSeverityNamespace.trim();
    document.aggregate_severity = agg;
  } else {
    delete document.aggregate_severity;
  }

  const notes = mergeDocNotes(form, asArr(srcDoc.notes));
  if (notes.length) document.notes = notes;
  else delete document.notes;

  const references = buildReferences(form.selfUrl, form.documentReferences);
  if (references.length) document.references = references;
  else delete document.references;

  const acks = buildAcks(form.acknowledgments);
  if (acks.length) document.acknowledgments = acks;
  else delete document.acknowledgments;

  out.document = document;

  // ---- product_tree ----------------------------------------------------
  const products = form.products.filter(
    (p) => p.name.trim() || Object.keys(asObj(p._source)).length,
  );
  const productIds = products.map((p) => p.id);
  const tree: Json = { ...asObj(out.product_tree) };
  // The flat product list is the canonical product representation; branches are
  // an alternative encoding of the same products, so re-emit as
  // full_product_names to avoid duplicate product_id definitions.
  delete tree.branches;
  if (products.length) {
    tree.full_product_names = products.map((p) => {
      const fpn: Json = { ...asObj(p._source) };
      fpn.product_id = p.id;
      fpn.name = p.name.trim() || asStr(fpn.name) || p.id;
      return fpn;
    });
  } else {
    delete tree.full_product_names;
  }
  if (Object.keys(tree).length) out.product_tree = tree;
  else delete out.product_tree;

  // ---- vulnerabilities -------------------------------------------------
  const vulns = form.vulnerabilities
    .filter(
      (v) =>
        v.cve.trim() ||
        v.title.trim() ||
        v.description.trim() ||
        Object.keys(asObj(v._source)).length,
    )
    .map((v) => buildVuln(v, productIds));
  if (vulns.length) out.vulnerabilities = vulns;
  else delete out.vulnerabilities;

  return out;
}

// ---------------------------------------------------------------------------
// CSAF → form (captures source for lossless round-trip)
// ---------------------------------------------------------------------------
function collectProductNames(csaf: Json): AdvisoryFormProduct[] {
  const products: AdvisoryFormProduct[] = [];
  const seen = new Set<string>();
  const push = (id: string, name: string, source: Json) => {
    if (id && !seen.has(id)) {
      seen.add(id);
      products.push(
        Object.keys(source).length
          ? { id, name, _source: source }
          : { id, name },
      );
    }
  };
  const tree = asObj(csaf.product_tree);
  const visit = (branch: Json) => {
    const product = asObj(branch.product);
    const id = asStr(product.product_id);
    if (id) push(id, asStr(product.name) || asStr(branch.name), product);
    for (const child of asArr(branch.branches)) visit(asObj(child));
  };
  for (const b of asArr(tree.branches)) visit(asObj(b));
  for (const fpn of asArr(tree.full_product_names)) {
    const p = asObj(fpn);
    push(asStr(p.product_id), asStr(p.name), p);
  }
  for (const rel of asArr(tree.relationships)) {
    const full = asObj(asObj(rel).full_product_name);
    push(asStr(full.product_id), asStr(full.name), full);
  }
  return products;
}

function noteText(notes: unknown[], category: string): string {
  for (const n of notes) {
    const o = asObj(n);
    if (asStr(o.category) === category) return asStr(o.text);
  }
  return "";
}

function statusList(status: Json, csafKey: string): string[] {
  return asArr(status[csafKey]).map(asStr).filter(Boolean);
}

function referencesFromCsaf(refs: unknown[]): {
  selfUrl: string;
  list: AdvisoryFormReference[];
} {
  let selfUrl = "";
  const list: AdvisoryFormReference[] = [];
  for (const ref of refs) {
    const r = asObj(ref);
    const category = asStr(r.category) || "external";
    const url = asStr(r.url);
    if (category === "self" && !selfUrl) {
      selfUrl = url;
      continue;
    }
    list.push({ category, summary: asStr(r.summary), url });
  }
  return { selfUrl, list };
}

function vulnFromCsaf(vuln: unknown): AdvisoryFormVulnerability {
  const v = asObj(vuln);
  const cwe = asObj(v.cwe);
  const status = asObj(v.product_status);
  const description = noteText(asArr(v.notes), "description");

  let cvssVersion = "3.1";
  let cvssVector = "";
  let cvssScore = "";
  for (const s of asArr(v.scores)) {
    const c = asObj(asObj(s).cvss_v3);
    if (Object.keys(c).length > 0) {
      cvssVersion = asStr(c.version) || "3.1";
      cvssVector = asStr(c.vectorString);
      cvssScore = typeof c.baseScore === "number" ? String(c.baseScore) : "";
      break;
    }
  }

  let exploitation = "";
  for (const t of asArr(v.threats)) {
    const o = asObj(t);
    if (asStr(o.category) === "exploit_status") {
      exploitation = asStr(o.details);
      break;
    }
  }

  const ids = asArr(v.ids).map((i) => {
    const o = asObj(i);
    return { systemName: asStr(o.system_name), text: asStr(o.text) };
  });

  const references = asArr(v.references).map((r) => {
    const o = asObj(r);
    return {
      category: asStr(o.category) || "external",
      summary: asStr(o.summary),
      url: asStr(o.url),
    };
  });

  const remediations = asArr(v.remediations).map((rem) => {
    const r = asObj(rem);
    return {
      category: asStr(r.category) || "vendor_fix",
      details: asStr(r.details),
      url: asStr(r.url),
      date: asStr(r.date),
      productIds: asArr(r.product_ids).map(asStr).filter(Boolean),
      _source: clone(r),
    };
  });

  return {
    cve: asStr(v.cve),
    title: asStr(v.title),
    cweId: asStr(cwe.id),
    cweName: asStr(cwe.name),
    description,
    cvssVersion,
    cvssVector,
    cvssScore,
    knownAffected: statusList(status, "known_affected"),
    knownNotAffected: statusList(status, "known_not_affected"),
    fixed: statusList(status, "fixed"),
    firstAffected: statusList(status, "first_affected"),
    lastAffected: statusList(status, "last_affected"),
    firstFixed: statusList(status, "first_fixed"),
    recommended: statusList(status, "recommended"),
    underInvestigation: statusList(status, "under_investigation"),
    exploitation,
    discoveryDate: asStr(v.discovery_date),
    releaseDate: asStr(v.release_date),
    ids,
    references,
    remediations,
    _source: clone(v),
  };
}

export function csafToForm(csaf: Json): AdvisoryForm {
  const base = emptyForm();
  const doc = asObj(csaf.document);
  const tracking = asObj(doc.tracking);
  const publisher = asObj(doc.publisher);
  const distribution = asObj(doc.distribution);
  const tlp = asObj(distribution.tlp);
  const agg = asObj(doc.aggregate_severity);
  const notes = asArr(doc.notes);

  const products = collectProductNames(csaf);
  const { selfUrl, list: documentReferences } = referencesFromCsaf(
    asArr(doc.references),
  );

  const revisionHistory = asArr(tracking.revision_history).map((rev) => {
    const r = asObj(rev);
    return {
      number: asStr(r.number),
      date: asStr(r.date),
      summary: asStr(r.summary),
    };
  });

  const acknowledgments = asArr(doc.acknowledgments).map((a) => {
    const o = asObj(a);
    return {
      names: joinCsv(asArr(o.names)),
      organization: asStr(o.organization),
      summary: asStr(o.summary),
      urls: joinCsv(asArr(o.urls)),
    };
  });

  const vulnerabilities = asArr(csaf.vulnerabilities).map(vulnFromCsaf);

  return {
    ...base,
    title: asStr(doc.title),
    tlp: asStr(tlp.label) || "WHITE",
    trackingId: asStr(tracking.id),
    status: asStr(tracking.status) || "draft",
    version: asStr(tracking.version) || "1",
    initialReleaseDate:
      asStr(tracking.initial_release_date) || base.initialReleaseDate,
    currentReleaseDate:
      asStr(tracking.current_release_date) || base.currentReleaseDate,
    aliases: joinCsv(asArr(tracking.aliases)),
    publisherName: asStr(publisher.name) || base.publisherName,
    publisherNamespace: asStr(publisher.namespace) || base.publisherNamespace,
    publisherContact: asStr(publisher.contact_details),
    issuingAuthority: asStr(publisher.issuing_authority),
    lang: asStr(doc.lang) || "en-US",
    aggregateSeverityText: asStr(agg.text),
    aggregateSeverityNamespace: asStr(agg.namespace),
    distributionText: asStr(distribution.text),
    rationale: noteText(notes, "summary"),
    notice: noteText(notes, "general"),
    disclaimer: noteText(notes, "legal_disclaimer") || DEFAULT_DISCLAIMER,
    selfUrl,
    documentReferences,
    products,
    vulnerabilities,
    acknowledgments,
    revisionHistory:
      revisionHistory.length > 0 ? revisionHistory : base.revisionHistory,
    _source: clone(csaf),
  };
}
